import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Minus, Plus } from 'lucide-react';

/**
 * Menú "Generar carrusel" — panel lateral en desktop, bottom sheet con
 * drag-to-dismiss en mobile, con el stepper de cuántas imágenes generar.
 *
 * Extraído de `pages/GeneratedImagesHistory` para que el historial y
 * `GeneratedImagesInline` compartan UNA implementación
 * (ADR-2026-08-11-carrusel-menu-compartido). Antes el menú vivía embebido en el
 * historial y el inline se había quedado con un `CarruselConfirmModal` viejo que
 * ni siquiera dejaba elegir la cantidad: exactamente el costo de tener el mismo
 * menú duplicado y actualizar una sola copia.
 *
 * El componente se queda con lo que es suyo —la cantidad, la animación y el
 * drag— y hacia afuera sólo expone "se cerró" y "se confirmó con N". Quien lo
 * usa no necesita saber nada del sheet.
 */

/** Mínimo del stepper: un carrusel de menos de 2 no es un carrusel. */
const CANTIDAD_MIN = 2;
const CANTIDAD_MAX = 10;
/** Debe coincidir con las `duration-300` de las transiciones de abajo. */
const DURACION_MS = 300;
/** Píxeles de arrastre a partir de los cuales el sheet mobile se cierra. */
const DRAG_CIERRE_PX = 100;

export interface CarruselMenuProps {
  open: boolean;
  onClose: () => void;
  /** Se dispara con la cantidad elegida en el stepper. */
  onConfirm: (cantidad: number) => void;
}

export const CarruselMenu: React.FC<CarruselMenuProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  // `montado` y `animado` van separados a propósito: al cerrar hay que dejar
  // correr la transición de salida ANTES de desmontar, si no el panel
  // desaparece de golpe en vez de deslizarse.
  const [montado, setMontado] = useState(false);
  const [animado, setAnimado] = useState(false);
  const [cantidad, setCantidad] = useState(CANTIDAD_MIN);
  const [dragY, setDragY] = useState(0);
  const dragStartY = useRef<number | null>(null);

  useEffect(() => {
    if (open) {
      // Cada apertura arranca limpia: la cantidad no se hereda de la vez pasada.
      setCantidad(CANTIDAD_MIN);
      setDragY(0);
      setMontado(true);
      return;
    }
    setAnimado(false);
    const id = setTimeout(() => {
      setMontado(false);
      setDragY(0);
    }, DURACION_MS);
    return () => clearTimeout(id);
  }, [open]);

  // El `requestAnimationFrame` va en un efecto aparte, disparado por `montado`:
  // encadenarlo al `setMontado(true)` de arriba deja que React batchee los dos
  // estados en el mismo tick y el panel aparecería YA animado, sin transición.
  useEffect(() => {
    if (!montado) return;
    const id = requestAnimationFrame(() => setAnimado(true));
    return () => cancelAnimationFrame(id);
  }, [montado]);

  if (!montado) return null;

  const menos = () => setCantidad((n) => Math.max(CANTIDAD_MIN, n - 1));
  const mas = () => setCantidad((n) => Math.min(CANTIDAD_MAX, n + 1));

  // Sección "Cantidad" con stepper +/- — compartida por el panel desktop y el
  // bottom-sheet mobile.
  const stepper = (
    <div>
      {/* Descripción — la que tenía el popup viejo (CarruselConfirmModal), ahora
          prominente arriba del stepper. Compartida por desktop y mobile. */}
      <p className="text-[15px] text-brand-black/90 leading-snug mb-5">
        Se generarán <span className="font-semibold">{cantidad} imágenes nuevas</span> a
        partir de esta foto, con distintos ángulos y variaciones.
      </p>
      <p className="text-[13px] font-normal text-[#3C3C43]/60 uppercase tracking-wide mb-2">
        Cantidad
      </p>
      <div className="rounded-[13px] bg-white overflow-hidden border border-neutral-200">
        <div className="px-4 py-[11px] flex items-center justify-between">
          <span className="text-[15px] text-brand-black/90">Imágenes a generar</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={menos}
              disabled={cantidad <= CANTIDAD_MIN}
              aria-label="Menos"
              className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-neutral-100 text-brand-black/70 hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="text-[16px] font-semibold text-brand-black/90 w-5 text-center tabular-nums">
              {cantidad}
            </span>
            <button
              type="button"
              onClick={mas}
              disabled={cantidad >= CANTIDAD_MAX}
              aria-label="Más"
              className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-neutral-100 text-brand-black/70 hover:bg-neutral-200 active:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const ilustracion = (
    <div className="flex justify-center mb-6">
      <img
        src="/images/carrusel.png"
        alt="Ejemplo de carrusel"
        className="w-4/5 object-contain"
      />
    </div>
  );

  // Portaleado a document.body: el lightbox de las galerías también se
  // renderiza con portal en el contexto raíz. Inline dentro del shell
  // `transform-gpu` de la app, el z-index queda atrapado en ese stacking
  // context y el menú aparecería POR DEBAJO del lightbox.
  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-[200] bg-black/40 transition-opacity duration-300 ${animado ? 'opacity-100' : 'opacity-0'}`}
        onClick={onClose}
      />
      {/* Desktop: side panel */}
      <div
        className={`hidden sm:flex fixed top-4 right-4 bottom-4 w-[420px] bg-white rounded-[13px] shadow-[0_8px_40px_rgba(0,0,0,0.12),0_0_0_0.5px_rgba(0,0,0,0.06)] z-[201] flex-col overflow-hidden transition-transform duration-300 ease-out ${animado ? 'translate-x-0' : 'translate-x-[calc(100%+32px)]'}`}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <h3 className="text-[17px] font-semibold text-brand-black/90">
            Generar carrusel
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08]" />
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {ilustracion}
          {stepper}
        </div>
        <div className="px-5 py-3 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(cantidad)}
            className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors"
          >
            Generar
          </button>
        </div>
      </div>
      {/* Mobile: bottom sheet con drag-to-dismiss */}
      <div
        className="sm:hidden fixed inset-x-0 bottom-0 z-[201] bg-[#F2F2F7] rounded-t-[13px] flex flex-col"
        style={{
          maxHeight: 'calc(100dvh - 1rem - env(safe-area-inset-top, 0px))',
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          transform:
            dragY > 0
              ? `translateY(${dragY}px)`
              : animado
                ? 'translateY(0)'
                : 'translateY(100%)',
          // Durante el arrastre no hay transición: el sheet tiene que seguir el
          // dedo cuadro a cuadro.
          transition: dragY > 0 ? 'none' : 'transform 300ms ease-out',
        }}
      >
        <div
          className="flex justify-center pt-2 pb-1 cursor-grab active:cursor-grabbing shrink-0"
          onTouchStart={(e) => {
            dragStartY.current = e.touches[0].clientY;
          }}
          onTouchMove={(e) => {
            if (dragStartY.current !== null) {
              const dy = e.touches[0].clientY - dragStartY.current;
              if (dy > 0) setDragY(dy);
            }
          }}
          onTouchEnd={() => {
            dragStartY.current = null;
            if (dragY > DRAG_CIERRE_PX) onClose();
            else setDragY(0);
          }}
        >
          <div className="w-9 h-[5px] rounded-full bg-neutral-300" />
        </div>
        <div className="flex items-center justify-between px-5 py-3 shrink-0">
          <h3 className="text-[15px] font-semibold text-brand-black/90">
            Generar carrusel
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 active:bg-black/[0.1] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {ilustracion}
          {stepper}
        </div>
        {/* Footer — solo "Generar" full-width; la X de arriba cierra. */}
        <div className="px-5 py-3 shrink-0">
          <button
            type="button"
            onClick={() => onConfirm(cantidad)}
            className="w-full text-[15px] font-semibold text-white bg-brand-primary active:bg-brand-primary-hover rounded-md px-4 py-3 shadow-sm transition-colors"
          >
            Generar
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default CarruselMenu;
